import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowBigDown,
  Award,
  Bandage,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  EyeOff,
  GitMerge,
  GraduationCap,
  Maximize2,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Mountain,
  Plus,
  RefreshCw,
  Scissors,
  Shuffle,
  Sparkles,
  Wind,
  ArrowRight,
} from "lucide-react";

// Constants

// Breadcrumb Component
const Breadcrumb = () => {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Rhinoplasty", href: "/rhinoplasty-in-mumbai" },
  ];

  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
 className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">
              Cosmetic Surgery · Face
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Rhinoplasty
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Nose Surgery by Dr. Vicky Jain · Rhinoplasty (Nose Reshaping / Nose Job)
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-certified">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-experience">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple" data-testid="hero-badge-training">International Training</span>
            </div>
          </div>
          
          {/* Right Column - Medical Line Illustration */}
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Rhinoplasty" icon="👃" size="hero"  imageSrc={HERO.rhinoplasty} imageAlt="Rhinoplasty"/>
          </div>
        </div>
      </div>
    </section>
  );
};

// What Is Section
const WhatIsSection = () => {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
              What is Rhinoplasty Surgery?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Rhinoplasty reshapes the nasal bones and cartilage to correct a hump, a crooked nose, a drooping or bulky tip, or wide nostrils. It is one of the most common facial plastic surgeries performed worldwide. When the septum is also involved, the procedure addresses breathing obstruction at the same time.
            </p>
            <p>
              Dr. Vicky Jain performs rhinoplasty for patients with cosmetic concerns, breathing problems, or both. The nose sits at the center of your face. A change here, even a small one, shifts how the entire face reads.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Dr. Jain's Approach Section
const DrJainApproachSection = () => {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain's Approach to Rhinoplasty
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Dr. Jain&apos;s approach starts with your anatomy, not a template. He uses open or closed rhinoplasty technique based on what your nose requires, not a default preference. For complex tip work or revision cases, open rhinoplasty gives the precision needed. For hump reduction or limited reshaping, closed technique keeps swelling timelines shorter.
              </p>
              <p>
                The goal is a nose that looks like yours, only improved. Over-correction is the most common cause of unnatural results. Dr. Jain works within your facial proportions to produce changes that read as natural to everyone who knows you.
              </p>
            </div>
          </div>
          
          <div className="relative">
            <div className="relative overflow-hidden">
              <img
                src="/dr-jain.avif"
                alt="Dr. Vicky Jain"
                loading="lazy"
                decoding="async"
                className="w-full h-[420px] object-cover object-top"
                data-testid="dr-jain-photo"
              />
              <div
                className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
                style={{
                  background: 'linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)'
                }}
              />
            </div>
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Nose Concerns Rhinoplasty Corrects
const NoseConcernsSection = () => {
  const cards = [
    {
      title: "Nasal Hump Removal",
      icon: Mountain,
      copy: "A dorsal hump is the most common reason patients seek rhinoplasty. Dr. Jain removes excess bone and cartilage from the bridge so the profile reads straight or gently curved from the side.",
    },
    {
      title: "Crooked or Asymmetric Nose",
      icon: Shuffle,
      copy: "Correction requires osteotomies (controlled repositioning of the nasal bones) along with cartilage work to bring the nose into alignment.",
    },
    {
      title: "Bulky or Drooping Nasal Tip",
      icon: ArrowBigDown,
      copy: "Dr. Jain uses suture techniques and cartilage grafting to refine, elevate, or define the tip while keeping the result natural.",
    },
    {
      title: "Wide Nostrils",
      icon: Maximize2,
      copy: "Nostril reduction removes a small wedge of tissue at the base of each nostril. Incisions are placed in the natural crease so scarring is not visible.",
    },
    {
      title: "Post-Injury Deformity",
      icon: Bandage,
      copy: "Nasal fractures often heal in a displaced position. Rhinoplasty corrects the resulting crooked profile, collapsed bridge, or internal airway obstruction.",
    },
    {
      title: "Breathing Difficulty",
      icon: Wind,
      copy: "Septorhinoplasty combines cosmetic reshaping with correction of the deviated septum, improving airflow without changing the external structure more than needed.",
    },
  ];

  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="nose-concerns-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Nose Concerns Rhinoplasty Corrects
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`nose-concern-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Types of Rhinoplasty Surgery
const ProcedureTypesSection = () => {
  const types = [
    {
      title: "Open Rhinoplasty",
      icon: Scissors,
      copy: "A small incision across the columella gives direct access to the nasal structure. Used for complex reshaping and tip work. The scar fades to near-invisible within 6 to 12 months.",
    },
    {
      title: "Closed Rhinoplasty",
      icon: EyeOff,
      copy: "All incisions inside the nostrils. No external scar. Works well for hump reduction or limited tip refinement, with shorter swelling timelines.",
    },
    {
      title: "Septorhinoplasty",
      icon: GitMerge,
      copy: "Combines cosmetic rhinoplasty with septoplasty. For patients who have both an aesthetic concern and a breathing problem blocked by a deviated septum.",
    },
    {
      title: "Revision Rhinoplasty",
      icon: RefreshCw,
      copy: "Corrects results from a previous nose surgery, whether the concern is cosmetic or functional. More complex than primary rhinoplasty due to altered anatomy and scar tissue.",
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="procedure-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">
          Types of Rhinoplasty Surgery
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {types.map((item, index) => (
            <div
              key={index}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`procedure-type-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <item.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Recovery Timeline Section
const RecoveryTimelineSection = () => {
  const milestones = [
    {
      period: "7 to 10 Days",
      description: "Splint comes off. Swelling and bruising present. Most patients return to desk work.",
      icon: BedDouble,
    },
    {
      period: "2 to 4 Weeks",
      description: "Visible bruising resolves. Avoid strenuous activity. Social presentability improves.",
      icon: Monitor,
    },
    {
      period: "3 Months",
      description: "Major swelling subsides. The nose is close to its final shape. Most patients are comfortable in photos.",
      icon: Dumbbell,
    },
    {
      period: "Up to 1 Year",
      description: "Fine swelling at the tip fully resolves. Final results assessed at 12 months post-surgery.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">
          Recovery Timeline
        </h2>
        
        {/* Desktop Timeline */}
        <div className="hidden md:block relative">
          {/* Timeline Line */}
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`timeline-milestone-${index}`}>
                {/* Icon above */}
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                {/* Gold Dot */}
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                {/* Content */}
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Mobile Timeline */}
        <div className="md:hidden relative pl-12">
          {/* Vertical Line */}
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative" data-testid={`timeline-mobile-${index}`}>
                {/* Gold Dot with Icon */}
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                {/* Content */}
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Who Is a Good Candidate
const WhoIsGoodCandidateSection = () => (
  <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-good-candidate-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
            Who Is a Good Candidate for Rhinoplasty?
          </h2>
        </div>
        <div className="space-y-6 text-deep-navy/70 leading-relaxed">
          <p>
            Most patients are between 18 and 45, have finished facial growth, and are in good general health. Dr. Jain bases the decision to operate on your anatomy, your specific concerns, and what is structurally achievable.
          </p>
          <p>
            You are likely a candidate if you want to correct a nasal hump, straighten a crooked nose, refine a bulky tip, reduce nostril width, or improve airflow blocked by a deviated septum. Past nasal fractures that left deformity are also treatable. Rhinoplasty improves the nose you have. It does not produce someone else&apos;s nose.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PatientResultsSection = () => <PatientTestimonials procedureName="Rhinoplasty" />;

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What is the suitable age group for rhinoplasty?",
      answer:
        "Most surgeons wait until facial growth is complete: 16 to 17 for female patients, 17 to 18 for male patients. There is no upper age limit for healthy adults.",
    },
    {
      question: "What does rhinoplasty cost?",
      answer:
        "Cost depends on procedure complexity, technique used, and whether functional correction is needed alongside cosmetic reshaping. Dr. Jain's team provides a detailed cost breakdown during consultation.",
    },
    {
      question: "What anesthesia is used for rhinoplasty?",
      answer: "General anesthesia in most cases. Surgery takes 1.5 to 3 hours depending on complexity.",
    },
    {
      question: "What tests are required before rhinoplasty surgery?",
      answer:
        "Standard pre-operative blood work, a nasal examination, and in some cases imaging. Dr. Jain's team provides a complete pre-surgery checklist at consultation.",
    },
    {
      question: "Is rhinoplasty painful?",
      answer:
        "Surgery is performed under anesthesia, so there is no pain during the procedure. The first 48 hours post-surgery involve mild discomfort and nasal congestion, managed with standard medication.",
    },
    {
      question: "How long does rhinoplasty recovery take?",
      answer:
        "Most patients return to work within 7 to 10 days. Final results develop over 6 to 12 months as swelling fully resolves.",
    },
    {
      question: "Will my results look natural?",
      answer:
        "Natural results come from working within your facial proportions, not overriding them. Over-correction is the primary cause of unnatural outcomes. Dr. Jain's approach avoids this.",
    },
    {
      question: "Is rhinoplasty permanent?",
      answer: "Yes. Structural changes to bone and cartilage are permanent.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Frequently Asked Questions
        </h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className="border-b border-deep-navy/10"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Consultation CTA Section
const ConsultationCTASection = () => {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Rhinoplasty Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines your nasal structure, discusses your concerns, and builds a surgical plan around your anatomy. You leave with a clear picture of what surgery involves, what results are achievable, and what recovery looks like for you.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
        
      </div>
    </section>
  );
};

// Related Procedures Section
const RelatedProceduresSection = () => {
  const procedures = [
    {
      name: "Septoplasty",
      description: "Surgical correction of a deviated septum to improve breathing function.",
      href: "/procedures/rhinoplasty"
    },
    {
      name: "Chin Augmentation",
      description: "Chin implant and genioplasty to balance profile with the nose and jawline.",
      href: "/chin-augmentation-in-mumbai"
    },
    {
      name: "Facelift",
      description: "Comprehensive facial rejuvenation addressing sagging skin and deep wrinkles.",
      href: "/facelift-surgery-mumbai"
    }
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Related Procedures
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure, index) => (
            <div 
              key={index} 
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
              data-testid={`related-procedure-${index}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <a 
                href={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const rhinoplastyBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://drvickyjain.com/" },
    { "@type": "ListItem", position: 2, name: "Cosmetic Surgery", item: "https://drvickyjain.com/cosmetic-surgery-in-mumbai" },
    { "@type": "ListItem", position: 3, name: "Rhinoplasty", item: "https://drvickyjain.com/rhinoplasty-in-mumbai" },
  ],
};

const rhinoplastyStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Rhinoplasty (Nose Reshaping Surgery)",
      alternateName: ["Nose job", "Nose reshaping", "Nasal surgery"],
      description: "Surgical reshaping of the nose to correct humps, crooked nose, drooping tip, wide nostrils, or breathing problems using open or closed rhinoplasty technique.",
      procedureType: "Surgical",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Nose" },
      followup: "Days 1–7: splint worn, mild swelling and congestion. Week 1–2: return to desk work. Month 1: most visible swelling resolved. Months 6–12: final results fully visible.",
      howPerformed: "General anesthesia; open or closed incision; cartilage and bone restructured; tip refined; splint applied; 1.5 to 3 hours; same-day or overnight discharge.",
      url: "https://drvickyjain.com/rhinoplasty-in-mumbai",
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "What is the suitable age group for rhinoplasty?", acceptedAnswer: { "@type": "Answer", text: "Most surgeons wait until facial growth is complete: 16 to 17 for female patients, 17 to 18 for male patients. There is no upper age limit for healthy adults." } },
        { "@type": "Question", name: "What does rhinoplasty cost?", acceptedAnswer: { "@type": "Answer", text: "Cost depends on procedure complexity, technique used, and whether functional correction is needed alongside cosmetic reshaping. Dr. Jain's team provides a detailed cost breakdown during consultation." } },
        { "@type": "Question", name: "What anesthesia is used for rhinoplasty?", acceptedAnswer: { "@type": "Answer", text: "General anesthesia in most cases. Surgery takes 1.5 to 3 hours depending on complexity." } },
        { "@type": "Question", name: "What tests are required before rhinoplasty surgery?", acceptedAnswer: { "@type": "Answer", text: "Standard pre-operative blood work, a nasal examination, and in some cases imaging. Dr. Jain's team provides a complete pre-surgery checklist at consultation." } },
        { "@type": "Question", name: "Is rhinoplasty painful?", acceptedAnswer: { "@type": "Answer", text: "Surgery is performed under anesthesia, so there is no pain during the procedure. The first 48 hours post-surgery involve mild discomfort and nasal congestion, managed with standard medication." } },
        { "@type": "Question", name: "How long does rhinoplasty recovery take?", acceptedAnswer: { "@type": "Answer", text: "Most patients return to work within 7 to 10 days. Final results develop over 6 to 12 months as swelling fully resolves." } },
        { "@type": "Question", name: "Will my results look natural?", acceptedAnswer: { "@type": "Answer", text: "Natural results come from working within your facial proportions, not overriding them. Over-correction is the primary cause of unnatural outcomes. Dr. Jain's approach avoids this." } },
        { "@type": "Question", name: "Is rhinoplasty permanent?", acceptedAnswer: { "@type": "Answer", text: "Yes. Structural changes to bone and cartilage are permanent." } },
      ],
    },
  ],
};

// Main Page Component
const RhinoplastyPage = () => {
  return (
    <>
      <SEO
        title="Rhinoplasty | Dr. Vicky Jain"
        description="Reshape your nose with rhinoplasty. Dr. Vicky Jain corrects humps, crooked noses, drooping tips, and breathing problems. Book your consultation."
        canonical="/rhinoplasty-in-mumbai"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(rhinoplastyBreadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(rhinoplastyStructuredData) }} />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <NoseConcernsSection />
      <ProcedureTypesSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
};

export default RhinoplastyPage;
